import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  InputAdornment,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  RestoreFromTrash as RestoreIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { Client, ClientCreate, ClientUpdate } from '../types/client';
import { clientsAPI } from '../services/api';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import { extractErrorMessage } from '../utils/errorHandler';
import CustomTablePagination from './CustomTablePagination';
import PermissionButton from './PermissionButton';
import PriceInput, { parsePriceValue } from './PriceInput';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';

const CONFIG_COLOR = '#1976D2';

const CLIENT_FILTER_SCHEMA = {
  code:         { type: 'text', param: 'code' },
  name:         { type: 'text', param: 'name' },
  ice:          { type: 'text', param: 'ice' },
  contact_name: { type: 'text', param: 'contact_name' },
  email:        { type: 'text', param: 'email' },
  phone:        { type: 'text', param: 'phone' },
  mobile:       { type: 'text', param: 'mobile' },
  address:      { type: 'text', param: 'address' },
  plafond:      { type: 'numberRange', paramMin: 'plafond_min', paramMax: 'plafond_max' },
  is_active:    { type: 'select', param: 'is_active' },
} as const;

const CLIENT_STATUT_OPTIONS = [
  { value: 'true', label: 'Actif' },
  { value: 'false', label: 'Inactif' },
];

const ClientManagement: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [deletedClients, setDeletedClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalClients, setTotalClients] = useState(0);
  const [totalDeletedClients, setTotalDeletedClients] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(CLIENT_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    ice: string;
    contact_name: string;
    email: string;
    phone: string;
    mobile: string;
    address: string;
    plafond: number | null | string;
    is_active: boolean;
  }>({
    code: '',
    name: '',
    ice: '',
    contact_name: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    plafond: null,
    is_active: true,
  });

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, filters]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const extra = toQueryParams();
      const data = await clientsAPI.getAll(
        page * rowsPerPage,
        rowsPerPage,
        searchTerm || undefined,
        undefined,
        Object.keys(extra).length > 0 ? extra : undefined,
      );
      setClients(data.items);
      setTotalClients(data.total);
    } catch (err) {
      showError('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedClients = async () => {
    try {
      setLoading(true);
      const data = await clientsAPI.getDeleted();
      setDeletedClients(data.items);
      setTotalDeletedClients(data.total);
    } catch (err) {
      showError('Erreur lors du chargement des clients supprimés');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClient = async (client: Client) => {
    try {
      await clientsAPI.restore(client.id);
      showSuccess('Client restauré avec succès');
      fetchDeletedClients();
      fetchClients();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) {
      fetchDeletedClients();
    }
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.name) {
      showError('Le code et le nom sont obligatoires');
      return;
    }

    try {
      // Prepare data: convert empty strings to undefined for optional fields
      const preparedData = {
        ...formData,
        ice: formData.ice?.trim() || undefined,
        contact_name: formData.contact_name?.trim() || undefined,
        email: formData.email?.trim() || undefined,
        phone: formData.phone?.trim() || undefined,
        mobile: formData.mobile?.trim() || undefined,
        address: formData.address?.trim() || undefined,
        plafond: parsePriceValue(formData.plafond),
      };

      if (selectedClient) {
        await clientsAPI.update(selectedClient.id, preparedData);
        showSuccess('Client modifié avec succès');
      } else {
        await clientsAPI.create(preparedData);
        showSuccess('Client créé avec succès');
      }

      setIsFormOpen(false);
      resetForm();
      fetchClients();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleDelete = async (client: Client) => {
    try {
      await clientsAPI.delete(client.id);
      showSuccess('Client supprimé avec succès');
      fetchClients();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleToggleStatus = async (client: Client) => {
    try {
      const result = await clientsAPI.toggleStatus(client.id);
      showSuccess(result.message);
      fetchClients();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      ice: '',
      contact_name: '',
      email: '',
      phone: '',
      mobile: '',
      address: '',
      plafond: null,
      is_active: true,
    });
    setSelectedClient(null);
  };

  const openForm = async (client?: Client) => {
    if (client) {
      setSelectedClient(client);
      setFormData({
        code: client.code,
        name: client.name,
        ice: client.ice || '',
        contact_name: client.contact_name || '',
        email: client.email || '',
        phone: client.phone || '',
        mobile: client.mobile || '',
        address: client.address || '',
        plafond: client.plafond ?? null,
        is_active: client.is_active,
      });
    } else {
      setSelectedClient(null);
      // Get the next auto-generated code
      try {
        const { code } = await clientsAPI.getNextCode();
        setFormData({
          code: code,
          name: '',
          ice: '',
          contact_name: '',
          email: '',
          phone: '',
          mobile: '',
          address: '',
          plafond: null,
          is_active: true,
        });
      } catch (err: any) {
        showError('Erreur lors de la génération du code');
        resetForm();
      }
    }
    setIsFormOpen(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  return (
    <Box p={3} sx={{ width: '100%', maxWidth: 'none' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Gestion des Clients
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
          <PermissionButton
            moduleName="Config"
            operation="add"
            miniModuleName="Clients"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openForm()}
            sx={{
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            Nouveau Client
          </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Clients Actifs" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
      <>
      <Box mb={3}>
        <TextField
          fullWidth
          variant="filled"
          placeholder="Rechercher par nom, code, email ou téléphone..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ ...getModuleTableTheme('configuration'), width: '100%', maxWidth: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Code
                  <ColumnFilter type="text" value={filters.code} onChange={(v) => update('code', v)} label="code" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Nom
                  <ColumnFilter type="text" value={filters.name} onChange={(v) => update('name', v)} label="nom" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  ICE
                  <ColumnFilter type="text" value={filters.ice} onChange={(v) => update('ice', v)} label="ICE" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Contact
                  <ColumnFilter type="text" value={filters.contact_name} onChange={(v) => update('contact_name', v)} label="contact" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Email
                  <ColumnFilter type="text" value={filters.email} onChange={(v) => update('email', v)} label="email" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Téléphone
                  <ColumnFilter type="text" value={filters.phone} onChange={(v) => update('phone', v)} label="téléphone" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Mobile
                  <ColumnFilter type="text" value={filters.mobile} onChange={(v) => update('mobile', v)} label="mobile" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Adresse
                  <ColumnFilter type="text" value={filters.address} onChange={(v) => update('address', v)} label="adresse" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Plafond
                  <ColumnFilter type="numberRange" value={filters.plafond} onChange={(v) => update('plafond', v)} label="plafond" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Statut
                  <ColumnFilter type="select" value={filters.is_active} onChange={(v) => update('is_active', v)} options={CLIENT_STATUT_OPTIONS} label="statut" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  {searchTerm || hasActive() ? 'Aucun client trouvé pour cette recherche' : 'Aucun client trouvé'}
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell>{client.code}</TableCell>
                  <TableCell>{client.name}</TableCell>
                  <TableCell>{client.ice || '-'}</TableCell>
                  <TableCell>{client.contact_name || '-'}</TableCell>
                  <TableCell>{client.email || '-'}</TableCell>
                  <TableCell>{client.phone || '-'}</TableCell>
                  <TableCell>{client.mobile || '-'}</TableCell>
                  <TableCell>{client.address || '-'}</TableCell>
                  <TableCell align="right">
                    {client.plafond ? new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(client.plafond) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={client.is_active ? 'Actif' : 'Inactif'}
                      color={client.is_active ? 'success' : 'default'}
                      size="small"
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>

                      <PermissionButton
                        moduleName="Config"
                        operation="edit"
                        miniModuleName="Clients"
                        component="IconButton"
                        onClick={() => openForm(client)}
                        size="small"
                        sx={{
                          color: '#1976D2',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                          }
                        }}
                      >
                        <EditIcon />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="Config"
                        operation="delete"
                        miniModuleName="Clients"
                        component="IconButton"
                        onClick={() => {
                          showConfirm(
                            `Êtes-vous sûr de vouloir supprimer le client "${client.name}" ?`,
                            () => handleDelete(client),
                            {
                              title: 'Confirmer la suppression',
                              confirmText: 'Supprimer',
                              cancelText: 'Annuler'
                            }
                          );
                        }}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </PermissionButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={totalClients}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {tabValue === 1 && (
      <>
      <TableContainer component={Paper} sx={{ ...getModuleTableTheme('configuration'), width: '100%', maxWidth: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>ICE</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Téléphone</TableCell>
              <TableCell>Date de Suppression</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : deletedClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Aucun client supprimé
                </TableCell>
              </TableRow>
            ) : (
              deletedClients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell>{client.code}</TableCell>
                  <TableCell>{client.name}</TableCell>
                  <TableCell>{client.ice || '-'}</TableCell>
                  <TableCell>{client.contact_name || '-'}</TableCell>
                  <TableCell>{client.email || '-'}</TableCell>
                  <TableCell>{client.phone || '-'}</TableCell>
                  <TableCell>{client.deleted_at ? new Date(client.deleted_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => {
                        showConfirm(
                          `Êtes-vous sûr de vouloir restaurer le client "${client.name}" ?`,
                          () => handleRestoreClient(client),
                          {
                            title: 'Confirmer la restauration',
                            confirmText: 'Restaurer',
                            cancelText: 'Annuler'
                          }
                        );
                      }}
                      color="primary"
                      size="small"
                      title="Restaurer"
                    >
                      <RestoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={totalDeletedClients}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {/* Client Form Dialog */}
      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedClient ? 'Modifier le client' : 'Nouveau client'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2 }}>
              <TextField
                variant="filled"
                label="Code *"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
                disabled
                helperText={!selectedClient ? "Code auto-généré" : "Code non modifiable"}
              />
              <TextField
                variant="filled"
                label="Nom *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2 }}>
              <TextField
                variant="filled"
                label="ICE"
                value={formData.ice}
                onChange={(e) => setFormData({ ...formData, ice: e.target.value })}
                helperText="Identifiant Commun de l'Entreprise"
              />
              <TextField
                variant="filled"
                label="Nom du contact"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              />
            </Box>

            <TextField
              variant="filled"
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                variant="filled"
                label="Téléphone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
              <TextField
                variant="filled"
                label="Mobile"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              />
            </Box>

            <TextField
              variant="filled"
              label="Adresse"
              multiline
              rows={3}
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />

            <PriceInput
              value={formData.plafond}
              onChange={(value) => setFormData({ ...formData, plafond: value })}
              label="Plafond (Limite de crédit)"
              helperText="Limite de crédit autorisée pour ce client"
              currency="MAD"
              size="medium"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Client actif"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormOpen(false)}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ 
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            {selectedClient ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default ClientManagement;